import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { LoggerService } from '@services/logger.service';
import { Registry } from '@/registry.service';
import { Router } from '@angular/router';
import { UserService } from '@services/admin/user.service';
import { ToastrService } from 'ngx-toastr';
import { DynamicFormPopUpInterface } from '@components/dynamic-form/dynamic-form-pop-up-interface';
import { DynamicFormComponent } from '@components/dynamic-form/dynamic-form.component';
import { DynamicFormSubmitInterface } from '@components/dynamic-form/dynamic-form-submit-interface';
import { RentalReservationModel } from '@/models/rentals/rental-reservation.model';
import { RentalReservationService } from '@services/data-source-tables/rentals/rental-reservation.service';
import { RentalReservationFormSetting } from '@/form-settings/rentals/rental-reservation-form-setting';
import { BrandsService } from '@services/data-source-tables/brands.service';
import { BrandModel } from '@/models/tables/brand.model';
import { DropdownOption } from '@components/dynamic-form/dropdown/dropdown-option';
import { combineLatest, of, Subject, Subscription } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { DialogSelectClientRentComponent } from '@pages/rental-management/rent-practice-detail/rental-movements-list/dialog-form-rental-movement/dialog-select-client-rent/dialog-select-client-rent.component';
import { RentalClientsService } from '@services/data-source-tables/rentals/rental-clients.service';
import { RentalClientModel } from '@/models/rentals/rental-client-model';
import { RentalPracticeService } from '@services/data-source-tables/rentals/rental-practice.service';
import { RentalPracticeModel } from '@/models/rental-practice.model';
import { DialogLinkPracticeComponent } from '@pages/rental-management/rental-reservation-list/dialog-form-rental-reservation/dialog-link-practice/dialog-link-practice.component';

@Component({
  selector: 'app-rental-reservation-form',
  templateUrl: '../../../../../components/dynamic-form/dynamic-form.component.html',
  styleUrls: ['../../../../../components/dynamic-form/dynamic-form.component.scss'],
})
export class RentalReservationFormComponent
  extends DynamicFormComponent
  implements DynamicFormPopUpInterface, DynamicFormSubmitInterface, OnInit, OnDestroy
{
  @Output() closeDialogEvent = new EventEmitter<void>();
  @Input() isNew: boolean = true;
  @Input() reloadTrigger$: Subject<any>;

  private brands: DropdownOption[] = [];
  private rentalPractices: RentalPracticeModel[] = [];
  private marcaSub?: Subscription;
  private conflictSub?: Subscription;
  private freeVehicleSub?: Subscription;
  private suppressMarcaWatcher = false;
  private suppressFreeVehicleWatcher = false;
  private lockRentalPractice = false;
  private readonly UPCOMING_DAYS = 14;
  public conflicts: RentalReservationModel[] = [];
  public upcoming: RentalReservationModel[] = [];
  protected closeSelectClientEvent$: EventEmitter<RentalClientModel> =
    new EventEmitter<RentalClientModel>();

  constructor(
    protected toastr: ToastrService,
    protected router: Router,
    protected userService: UserService,
    protected rentalReservationService: RentalReservationService,
    protected dialogRef: MatDialog,
    protected rentalClientsService: RentalClientsService,
    protected rentalPracticeService: RentalPracticeService,
  ) {
    super(toastr, userService);
    this.data = new RentalReservationModel();
    this.primaryButton = 'Salva Prenotazione';
    this.submitTopButton = false;
    this.secondaryBottomButton = 'Annulla';
    this.thirdButton = 'Seleziona cliente';

    Registry.getInstance().set('RentalReservationFormComponent', this);

    this.loadBrands();
  }

  ngOnInit() {
    if (this.data && this.data.id) {
      this.isNew = false;

      if (!this.data.rentalPractice?.id) {
        this.lockRentalPractice = true;
        this.secondButton = 'Collega pratica';
      } else {
        this.lockRentalPractice = false;
        this.secondButton = undefined;
      }

      this.refreshForm();
      this.applyLocksFromCurrentValue();
      this.loadRentalPractices(this.data.marca?.id ?? this.data.rentalPractice?.marca?.id);
    } else {
      this.loadRentalPractices();
    }

    this.subscribeToMarcaChanges();
    this.subscribeToFreeVehicleChanges();
    this.subscribeToConflictChecks();
  }

  ngOnDestroy() {
    this.marcaSub?.unsubscribe();
    this.conflictSub?.unsubscribe();
    this.freeVehicleSub?.unsubscribe();
  }

  setupMeta() {
    this.meta = RentalReservationFormSetting.setIsNew(this.isNew)
      .setBrands(this.brands)
      .setRentalPractices(this.rentalPractices)
      .setLockRentalPractice(this.lockRentalPractice)
      .getMeta();
  }

  private loadBrands() {
    this.rentalReservationService.getBrands().subscribe((brands: BrandModel[]) => {
      this.brands = brands.map((brand) => ({
        key: brand,
        value: brand.codiceMarca + ' - ' + brand.descrizioneMarca,
      }));

      this.refreshFormPreservingValues();
    });
  }

  private loadRentalPractices(marcaId?: number) {
    this.rentalPracticeService
      .getActiveRentalPracticesByMarcaId(marcaId)
      .subscribe((practices: RentalPracticeModel[]) => {
        this.rentalPractices = practices;
        this.refreshFormPreservingValues();
      });
  }

  private subscribeToMarcaChanges() {
    this.marcaSub?.unsubscribe();

    const marcaControl = this.form?.get('marca');
    if (!marcaControl) {
      return;
    }

    this.marcaSub = marcaControl.valueChanges
      .pipe(distinctUntilChanged((a, b) => this.extractBrandId(a) === this.extractBrandId(b)))
      .subscribe((value) => {
        if (this.suppressMarcaWatcher) {
          return;
        }

        const marcaId = this.extractBrandId(value);

        this.form.get('rentalPractice')?.setValue(null, { emitEvent: false });
        this.form.get('veicoloPrenotazioneLibera')?.enable({ emitEvent: false });

        this.loadRentalPractices(marcaId ?? undefined);
      });
  }

  private subscribeToFreeVehicleChanges() {
    this.freeVehicleSub?.unsubscribe();

    const freeVehicleControl = this.form?.get('veicoloPrenotazioneLibera');
    if (!freeVehicleControl) {
      return;
    }

    this.freeVehicleSub = freeVehicleControl.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        if (this.suppressFreeVehicleWatcher) {
          return;
        }

        this.applyLocksFromCurrentValue();
      });
  }

  private extractBrandId(value: any): number | null {
    if (!value) return null;
    if (value.id != null) return value.id;
    if (value.key?.id != null) return value.key.id;
    return null;
  }

  private refreshFormPreservingValues() {
    const snapshot = this.form?.getRawValue() ?? {};
    this.data = { ...this.data, ...snapshot } as RentalReservationModel;
    this.refreshForm();
    this.subscribeToMarcaChanges();
    this.subscribeToFreeVehicleChanges();
    this.applyLocksFromCurrentValue();
    this.subscribeToConflictChecks();
  }

  private subscribeToConflictChecks() {
    this.conflictSub?.unsubscribe();

    const dataInizioCtrl = this.form?.get('dataInizio');
    const dataFineCtrl = this.form?.get('dataFine');
    const rentalPracticeCtrl = this.form?.get('rentalPractice');
    if (!dataInizioCtrl || !dataFineCtrl || !rentalPracticeCtrl) {
      return;
    }

    const dataInizio$ = dataInizioCtrl.valueChanges.pipe(startWith(dataInizioCtrl.value));
    const dataFine$ = dataFineCtrl.valueChanges.pipe(startWith(dataFineCtrl.value));
    const rentalPractice$ = rentalPracticeCtrl.valueChanges.pipe(
      startWith(rentalPracticeCtrl.value),
    );

    this.conflictSub = combineLatest([dataInizio$, dataFine$, rentalPractice$])
      .pipe(
        debounceTime(400),
        distinctUntilChanged(
          (a, b) =>
            this.sameDate(a[0], b[0]) &&
            this.sameDate(a[1], b[1]) &&
            this.extractPracticeId(a[2]) === this.extractPracticeId(b[2]),
        ),
        switchMap(([, , rentalPractice]) => {
          const practiceId = this.extractPracticeId(rentalPractice);
          if (practiceId == null) {
            this.conflicts = [];
            this.upcoming = [];
            return of([] as RentalReservationModel[]);
          }
          return this.rentalReservationService
            .getReservationsByPractice({
              rentalPracticeId: practiceId,
              excludeId: this.data?.id,
            })
            .pipe(
              catchError((error) => {
                LoggerService.error('getReservationsByPractice failed!');
                LoggerService.error(error);
                return of([] as RentalReservationModel[]);
              }),
            );
        }),
      )
      .subscribe((rows) => this.classify(rows));
  }

  private classify(rows: RentalReservationModel[]) {
    const dataInizio = this.toDate(this.form?.get('dataInizio')?.value);
    const dataFine = this.toDate(this.form?.get('dataFine')?.value);

    if (!dataInizio || !dataFine) {
      this.conflicts = [];
      this.upcoming = rows;
      return;
    }

    const upcomingEnd = this.addDays(dataFine, this.UPCOMING_DAYS);
    const conflicts: RentalReservationModel[] = [];
    const upcoming: RentalReservationModel[] = [];

    rows.forEach((r) => {
      const rStart = this.toDate(r.dataInizio);
      const rEnd = this.toDate(r.dataFine);
      if (!rStart || !rEnd) {
        return;
      }
      const overlaps = rStart <= dataFine && rEnd >= dataInizio;
      const isUpcoming = rStart > dataFine && rStart <= upcomingEnd;
      if (overlaps) conflicts.push(r);
      else if (isUpcoming) upcoming.push(r);
    });

    this.conflicts = conflicts;
    this.upcoming = upcoming;
  }

  private extractPracticeId(value: any): number | null {
    if (!value) return null;
    if (value.id != null) return value.id;
    if (value.key?.id != null) return value.key.id;
    return null;
  }

  private toDate(value: any): Date | null {
    if (value == null || value === '') return null;
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  private sameDate(a: any, b: any): boolean {
    const da = this.toDate(a);
    const db = this.toDate(b);
    if (!da && !db) return true;
    if (!da || !db) return false;
    return da.getTime() === db.getTime();
  }

  private addDays(date: Date, days: number): Date {
    const out = new Date(date);
    out.setDate(out.getDate() + days);
    return out;
  }

  private applyLocksFromCurrentValue() {
    if (!this.form) {
      return;
    }

    const marca = this.form.get('marca');
    const practiceCtrl = this.form.get('rentalPractice');
    const freeVehicleCtrl = this.form.get('veicoloPrenotazioneLibera');

    const practice = practiceCtrl?.value as RentalPracticeModel | null;
    const hasPractice = !!practice?.id;
    const freeVehicle = freeVehicleCtrl?.value;
    const hasFreeVehicle = typeof freeVehicle === 'string' ? freeVehicle.length > 0 : !!freeVehicle;

    this.suppressMarcaWatcher = true;
    this.suppressFreeVehicleWatcher = true;

    // Mutua esclusività: il controllo disabilitato è escluso dalla validazione,
    // così Validators.required scatta solo su quello attivo (uno dei due è obbligatorio).
    if (hasPractice) {
      if (practice.marca) {
        marca?.setValue(practice.marca, { emitEvent: false });
      }
      marca?.disable({ emitEvent: false });
      freeVehicleCtrl?.disable({ emitEvent: false });
      this.lockRentalPractice
        ? practiceCtrl?.disable({ emitEvent: false })
        : practiceCtrl?.enable({ emitEvent: false });
    } else if (hasFreeVehicle) {
      marca?.enable({ emitEvent: false });
      freeVehicleCtrl?.enable({ emitEvent: false });
      practiceCtrl?.disable({ emitEvent: false });
    } else {
      marca?.enable({ emitEvent: false });
      freeVehicleCtrl?.enable({ emitEvent: false });
      this.lockRentalPractice
        ? practiceCtrl?.disable({ emitEvent: false })
        : practiceCtrl?.enable({ emitEvent: false });
    }

    this.suppressMarcaWatcher = false;
    this.suppressFreeVehicleWatcher = false;
  }

  public onRentalPracticeSelected(practice: RentalPracticeModel | null) {
    if (!this.form) {
      return;
    }

    this.suppressMarcaWatcher = true;
    this.form.get('rentalPractice')?.setValue(practice ?? null, { emitEvent: false });
    this.suppressMarcaWatcher = false;

    this.applyLocksFromCurrentValue();
  }

  public onRentalPracticeCleared() {
    this.suppressMarcaWatcher = true;
    this.form?.get('rentalPractice')?.setValue(null, { emitEvent: false });
    this.suppressMarcaWatcher = false;

    this.applyLocksFromCurrentValue();
  }

  onSubmit() {
    this.submitButtonLoading = true;

    let reservationModel = <RentalReservationModel>this.form.getRawValue();

    this.rentalReservationService.saveReservation(reservationModel).subscribe({
      next: () => {
        this.toastr.success(
          this.isNew
            ? 'Prenotazione creata con successo!'
            : 'Prenotazione aggiornata con successo!',
        );

        if (this.reloadTrigger$) {
          this.reloadTrigger$.next(1);
        }

        this.bottomButtonAction();
      },
      error: (error) => {
        LoggerService.error('saveReservation failed!');
        LoggerService.error(error);

        const errorKey = error?.error?.key ?? '';

        if (errorKey === 'CONFLICT') {
          this.toastr.error(
            'Conflitto rilevato: esiste già una prenotazione o un noleggio attivo che si sovrappone alle date selezionate per il veicolo/pratica scelta. Modifica le date o seleziona un altro veicolo.',
            'Conflitto prenotazione',
            { timeOut: 8000 },
          );
        } else {
          this.toastr.error('Errore nel salvataggio della prenotazione!');
        }

        this.submitButtonLoading = false;
        Registry.getInstance().set('saveInProgress', false);
      },
      complete: () => {
        LoggerService.log('Request done');
      },
    });
  }

  bottomButtonAction(): void {
    this.closeDialogEvent.emit();
    this.submitButtonLoading = false;
  }

  protected secondButtonAction() {
    if (!this.data?.id) {
      return;
    }

    const marcaId = this.extractBrandId(this.form?.get('marca')?.value);
    const dialogData: { reservationId: number; marcaId?: number } = {
      reservationId: this.data.id,
    };
    if (marcaId != null) {
      dialogData.marcaId = marcaId;
    }

    const linkDialog = this.dialogRef.open(DialogLinkPracticeComponent, {
      data: dialogData,
      width: 'auto',
      panelClass: 'custom-modalbox',
    });

    linkDialog.afterClosed().subscribe((linked: boolean) => {
      if (linked) {
        if (this.reloadTrigger$) {
          this.reloadTrigger$.next(1);
        }
        this.bottomButtonAction();
      }
    });
  }

  protected thirdButtonAction() {
    Registry.getInstance().set('closeSelectClientEvent$', this.closeSelectClientEvent$);

    const dialogSelectClient = this.dialogRef.open(DialogSelectClientRentComponent, {
      data: {
        service: this.rentalClientsService,
      },
      width: 'auto',
      panelClass: 'custom-modalbox',
    });

    Registry.getInstance()
      .get('closeSelectClientEvent$')
      .subscribe((client) => {
        this.form.get('bpCliente').setValue(client.numeroBp);
        this.form.get('denominazioneCliente').setValue(client.denominazione);
        this.form.get('indirizzoCliente').setValue(client.indirizzo);
        this.form.get('cittaCliente').setValue(client.localita);
        this.form.get('telefonoCliente').setValue(client.numeroTelefono);
        this.form.get('emailCliente').setValue(client.email);

        dialogSelectClient.close();
      });
  }
}
